/**
 * TaskFormTab — Renders composable form fragments for task completion (DRR-173).
 *
 * When a task has formComponents[] (set by the rule that created it), this tab
 * fetches each fragment schema, merges them into a sectioned form, and renders.
 * On submit, calls completeTask which auto-creates output entities.
 *
 * Pipeline: Rule → Task.formComponents → fetch fragments → merge → FormRenderer → completeTask
 *
 * Falls back to single formSchemaRef for legacy tasks.
 * DUMB FRONTEND: All form fields come from backend FormSchema API (DRR-055).
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Badge, Button, Group, Stack, Text } from '@mantine/core';
import { IconForms, IconCheck } from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery as useGqlQuery, useMutation as useGqlMutation } from '@apollo/client/react';
import { schemaToFormDefinition } from '@/components/UniversalEntityForm/schemaToFormDef';
import { FormRenderer } from '@/renderer/FormRenderer';
import type { FormDefinition, FieldDefinition } from '@/renderer/types';
import type { FormSchema } from '@/generated/graphql';
import { GqlTaskStatus } from '@/generated/graphql';
import type { TabRendererProps } from '@ferp/workflow-forms';

// ── GraphQL ─────────────────────────────────────────────────────────────────

const COMPLETE_TASK_MUTATION = gql`
  mutation CompleteTask($workflowInstanceId: String!, $input: GqlCompleteTaskInput!) {
    completeTask(workflowInstanceId: $workflowInstanceId, input: $input) {
      id status
    }
  }
`;

/** Batch-fetch multiple form schema fragments by their schema IDs */
const FORM_FRAGMENTS_QUERY = gql`
  query FormFragments($fragmentIds: [String!]!) {
    formFragments(fragmentIds: $fragmentIds) {
      subgraph
      entityType
      version
      displayName
      description
      safetyLevel
      fields {
        name path fieldType label required placeholder
        description defaultValue readOnly hidden
        order section safetyLevel minimumTier
        options { value label }
        conditions { field operator value action targetField }
        validation { min max minLength maxLength pattern customRule errorMessage }
        referenceConfig { entityType displayField valueField queryName }
        metadata
      }
      layout {
        layoutType
        sections { id title description fields order collapsible collapsed }
      }
      validations { fields expression message }
      createMutation updateMutation fetchQuery
    }
  }
`;

// ── Fragment Merger (DRR-173) ───────────────────────────────────────────────

/**
 * Merges multiple FormDefinitions (from fragment schemas) into one
 * sectioned FormDefinition. Each fragment becomes a collapsible section.
 *
 * Pure function — no side effects, deterministic output.
 */
function mergeFormFragments(
  fragments: Array<{ id: string; title: string; definition: FormDefinition }>
): FormDefinition {
  const allFields: FieldDefinition[] = [];
  const sections: FormDefinition['sections'] = [];
  const seenFieldNames = new Set<string>();

  for (const fragment of fragments) {
    const fragmentFields: string[] = [];

    for (const field of fragment.definition.fields ?? []) {
      // Deduplicate: first fragment wins for same field name
      if (seenFieldNames.has(field.name)) continue;
      seenFieldNames.add(field.name);
      allFields.push(field);
      fragmentFields.push(field.name);
    }

    if (fragmentFields.length > 0) {
      sections.push({
        id: fragment.id,
        title: fragment.title,
        description: fragment.definition.description,
        fields: fragmentFields,
        collapsible: true,
        collapsed: false,
      });
    }
  }

  // Merge cross-field validations
  const crossFieldValidation = fragments.flatMap(
    f => f.definition.crossFieldValidation ?? []
  );

  return {
    id: `composed-${fragments.map(f => f.id).join('+')}`,
    version: '1.0.0',
    name: fragments.map(f => f.title).join(' + '),
    description: `Composed form: ${fragments.length} sections`,
    layout: 'sections' as FormDefinition['layout'],
    fields: allFields,
    sections,
    crossFieldValidation: crossFieldValidation.length > 0 ? crossFieldValidation : undefined,
    safetyLevel: fragments.some(f => f.definition.safetyLevel === 'life-critical')
      ? 'life-critical'
      : fragments.some(f => f.definition.safetyLevel === 'critical')
        ? 'critical'
        : 'standard',
  } as FormDefinition;
}

// ── Component ───────────────────────────────────────────────────────────────

export function TaskFormTab({ entity }: TabRendererProps) {
  const task = entity as Record<string, unknown>;
  const formSchemaRef = task.formSchemaRef as string | undefined;
  const formComponents = (task.formComponents ?? []) as string[];
  const formSchemaOverride = task.formSchemaOverride as string | undefined;
  const taskId = task.id as string;
  const taskStatus = task.status as string;
  const workflowInstanceId = (task.workflowInstanceId ?? '') as string;
  const outputEntities = (task.outputEntities ?? []) as Array<{ subgraph: string; entityType: string }>;

  const hasFragments = formComponents.length > 0;
  const hasForm = hasFragments || !!formSchemaRef;

  const [submitted, setSubmitted] = useState(false);

  // ── Fragment-based form loading (DRR-173) ──────────────────────────────

  const { data: fragmentsData, loading: fragmentsLoading } = useGqlQuery<{
    formFragments: FormSchema[];
  }>(FORM_FRAGMENTS_QUERY, {
    variables: { fragmentIds: formComponents },
    skip: !hasFragments,
    fetchPolicy: 'cache-first',
  });

  const composedForm = useMemo<FormDefinition | null>(() => {
    if (!hasFragments) return null;
    const schemas = fragmentsData?.formFragments;
    if (!schemas || schemas.length === 0) return null;

    // Convert each schema to FormDefinition, then merge
    const fragments = schemas.map((schema, idx) => ({
      id: formComponents[idx] ?? schema.entityType,
      title: schema.displayName ?? schema.entityType,
      definition: schemaToFormDefinition(schema),
    }));

    return mergeFormFragments(fragments);
  }, [fragmentsData, formComponents, hasFragments]);

  // ── Legacy single-schema loading ───────────────────────────────────────

  const [legacySubgraph, legacyEntityType] = useMemo(() => {
    if (hasFragments || !formSchemaRef) return ['', ''];
    const parts = formSchemaRef.split(':');
    if (parts.length === 2) return [parts[0], parts[1]];
    return ['core', formSchemaRef];
  }, [formSchemaRef, hasFragments]);

  const { data: legacyData, loading: legacyLoading } = useGqlQuery<{
    formSchema: FormSchema;
  }>(FORM_FRAGMENTS_QUERY, {
    variables: { fragmentIds: [] }, // Not used for legacy path
    skip: true, // Legacy uses separate query below
  });

  // For legacy: use inline query since useFormSchema hook has different shape
  const legacyForm = useMemo<FormDefinition | null>(() => {
    if (hasFragments || !legacyData?.formSchema) return null;
    return schemaToFormDefinition(legacyData.formSchema);
  }, [legacyData, hasFragments]);

  // Pick the active form definition
  const formDefinition = composedForm ?? legacyForm ?? null;
  const formLoading = hasFragments ? fragmentsLoading : legacyLoading;

  // ── Completion ─────────────────────────────────────────────────────────

  const [completeTask, { loading: completing }] = useGqlMutation(COMPLETE_TASK_MUTATION);

  const handleSubmit = useCallback(async (formData: Record<string, unknown>) => {
    try {
      await completeTask({
        variables: {
          workflowInstanceId,
          input: {
            taskId,
            outputData: JSON.stringify(formData),
          },
        },
        refetchQueries: ['GetTasksByWorkflowInstance', 'GetInstance'],
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Task completion failed:', err);
    }
  }, [completeTask, workflowInstanceId, taskId]);

  // ── Render ─────────────────────────────────────────────────────────────

  if (!hasForm) {
    return (
      <Alert color="gray" title="No Form Attached">
        This task does not have a form schema binding. Complete it via the status controls.
      </Alert>
    );
  }

  if (taskStatus === GqlTaskStatus.COMPLETED || submitted) {
    return (
      <Alert color="green" icon={<IconCheck size={16} />} title="Task Completed">
        This task has been completed.
        {outputEntities.length > 0 && (
          <Group gap="xs" mt="xs">
            <Text size="xs">Created:</Text>
            {outputEntities.map((e, i) => (
              <Badge key={i} size="xs" variant="light">{e.subgraph}/{e.entityType}</Badge>
            ))}
          </Group>
        )}
      </Alert>
    );
  }

  if (formLoading) {
    return <Text size="sm" c="dimmed">Loading form fragments...</Text>;
  }

  if (!formDefinition) {
    return (
      <Alert color="yellow" title="Form Not Available">
        {hasFragments
          ? `Form fragments [${formComponents.join(', ')}] could not be loaded.`
          : `Form schema "${formSchemaRef}" could not be loaded.`}
        {formSchemaOverride && <Text size="xs" mt="xs">Override: {formSchemaOverride}</Text>}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group gap="xs" wrap="wrap">
        <IconForms size={16} />
        <Text size="sm" fw={600}>Task Form</Text>
        {hasFragments ? (
          formComponents.map(id => (
            <Badge key={id} size="xs" variant="outline">{id}</Badge>
          ))
        ) : (
          <Badge size="xs" variant="outline">{formSchemaRef}</Badge>
        )}
        {formSchemaOverride && <Badge size="xs" variant="outline" color="blue">{formSchemaOverride}</Badge>}
      </Group>

      {outputEntities.length > 0 && (
        <Alert color="blue" variant="light" title="Submitting this form will create:">
          <Group gap="xs">
            {outputEntities.map((e, i) => (
              <Badge key={i} size="sm" variant="light">{e.subgraph}: {e.entityType}</Badge>
            ))}
          </Group>
        </Alert>
      )}

      <FormRenderer form={formDefinition} onSubmit={handleSubmit} />

      <Button
        type="submit"
        form="ferp-form-renderer"
        loading={completing}
        color="teal"
      >
        Complete Task
      </Button>
    </Stack>
  );
}
